/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { fetchChordFingerings, ChordFingering, ChordPosition } from '../api';
import { Guitar } from 'lucide-react';

interface MobileChordFingeringsProps {
  chordRoot: string;
  chordType: string;
  onFingeringSelect: (fingering: ChordFingering | null) => void;
  selectedFingering?: ChordFingering | null;
  fretRange?: { start: number; end: number };
}

/**
 * Check if a chord fingering is visible within the given fret range.
 * A fingering is visible if all its non-muted positions have frets within the range.
 */
const isFingeringVisible = (
  fingering: ChordFingering,
  fretRange: { start: number; end: number }
): boolean => {
  // Get all fretted positions (non-muted)
  const frettedPositions = fingering.positions.filter(p => !p.isMuted);

  if (frettedPositions.length === 0) return true;

  // Check if all fretted positions are within the fret range
  return frettedPositions.every(p => p.fret >= fretRange.start && p.fret <= fretRange.end);
};

const MobileChordFingerings: React.FC<MobileChordFingeringsProps> = ({
  chordRoot,
  chordType,
  onFingeringSelect,
  selectedFingering,
  fretRange
}) => {
  const [fingerings, setFingerings] = useState<ChordFingering[]>([]);
  const [loading, setLoading] = useState(false);

  function toCamelCasePosition(p: any): ChordPosition {
    return {
      stringIndex: p.StringIndex,
      fret: p.Fret,
      finger: p.Finger,
      isMuted: p.IsMuted,
      isOpen: p.IsOpen,
      note: p.Note
    };
  }

  function toCamelCaseFingering(f: any): ChordFingering {
    return {
      name: f.Name,
      positionString: f.PositionString,
      fingers: f.FingerString,
      startingFret: f.StartingFret,
      barreFret: f.BarreFret,
      positions: f.Positions ? f.Positions.map(toCamelCasePosition) : []
    };
  }

  useEffect(() => {
    const loadFingerings = async () => {
      if (!chordRoot || !chordType) return;

      setLoading(true);
      try {
        const response = await fetchChordFingerings(chordRoot, chordType);
        const fingerings = Array.isArray(response)
          ? response.map(toCamelCaseFingering)
          : [];
        setFingerings(fingerings);
      } catch (error) {
        console.error('Error loading fingerings:', error);
        setFingerings([]);
      } finally {
        setLoading(false);
      }
    };

    loadFingerings();
  }, [chordRoot, chordType]);

  // Auto-select the first visible fingering when fingerings load and none is selected
  useEffect(() => {
    // Only auto-select if:
    // 1. Not currently loading
    // 2. We have fingerings
    // 3. No fingering is currently selected
    if (loading || fingerings.length === 0 || selectedFingering !== null) {
      return;
    }

    // Filter by fret range visibility
    const visibleFingerings = fretRange
      ? fingerings.filter(f => isFingeringVisible(f, fretRange))
      : fingerings;

    // Auto-select the first visible fingering
    if (visibleFingerings.length > 0) {
      onFingeringSelect(visibleFingerings[0]);
    }
  }, [loading, fingerings, selectedFingering, fretRange, onFingeringSelect]);

  if (loading) {
    return (
      <div className="w-full py-3 px-4 bg-card border-t">
        <div className="text-xs text-muted-foreground text-center">
          Loading fingerings...
        </div>
      </div>
    );
  }

  // Filter fingerings based on fret range visibility
  const visibleFingerings = fretRange
    ? fingerings.filter(f => isFingeringVisible(f, fretRange))
    : fingerings;

  if (visibleFingerings.length === 0) {
    return null; // Don't show anything if no fingerings visible
  }

  return (
    <div className="w-full bg-card border-t">
      <div className="px-3 pt-2 pb-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Guitar className="h-3.5 w-3.5 text-muted-foreground" />
            <h3 className="text-xs font-semibold">Fingerings</h3>
          </div>
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {visibleFingerings.length}
          </Badge>
        </div>
      </div>

      {/* Grid Container - 2 rows, 3 columns */}
      <div className="px-3 pb-2">
        <div className="grid grid-cols-3 gap-2">
          {visibleFingerings.map((fingering, index) => (
            <Card
              key={index}
              className={`cursor-pointer transition-all ${
                selectedFingering === fingering
                  ? 'bg-primary/10 border-primary ring-1 ring-primary'
                  : 'hover:bg-muted/50 border-border'
              }`}
              onClick={() => {
                if (selectedFingering === fingering) {
                  onFingeringSelect(null); // Deselect if already selected
                } else {
                  onFingeringSelect(fingering);
                }
              }}
            >
              <CardContent className="p-2">
                {/* Fingering Name */}
                <div className="mb-1">
                  <h4 className="font-medium text-[10px] truncate leading-tight">
                    {fingering.name}
                  </h4>
                </div>

                {/* Position String */}
                <div className="mb-1 font-mono text-[9px] text-muted-foreground whitespace-nowrap overflow-hidden">
                  {fingering.positionString}
                </div>

                {/* Badges */}
                <div className="flex flex-wrap gap-1">
                  {fingering.startingFret > 1 && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">
                      Fr{fingering.startingFret}
                    </Badge>
                  )}
                  {selectedFingering === fingering && (
                    <Badge className="text-[9px] px-1 py-0 h-4 bg-primary">
                      ✓
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MobileChordFingerings;
